import { cloneElement, isValidElement, ReactElement, ReactNode } from 'react';
import { UpgradeTooltip } from './UpgradeTooltip';

interface GatedControlProps {
  /** Whether the control should render normally. When false, it renders disabled with an upgrade tooltip instead. */
  allowed: boolean;
  /** Shown in the upgrade tooltip when not allowed; falls back to UpgradeTooltip's default. */
  message?: string;
  /** Rendered as-is when `allowed`. */
  children: ReactNode;
  /**
   * Disabled-state UI to render (wrapped in the tooltip) when not allowed.
   * Defaults to cloning `children` with `disabled` forced on and `onClick`
   * stripped -- fine when `children` is itself a single control (a Button,
   * a DropdownMenuItem), but required when `children` is a larger subtree
   * (e.g. a whole `<Dialog>` whose trigger needs disabling, not the Dialog
   * itself).
   */
  fallback?: ReactElement;
}

/**
 * The generic primitive behind both `EntitlementGate` (plan/module/action
 * gating) and any quantity-limit gating (e.g. team-member seat caps): given
 * a plain `allowed` boolean, renders `children` untouched when true, or a
 * disabled control wrapped in `UpgradeTooltip` when false. Callers that
 * already know *why* something is or isn't allowed should compute that
 * reason and pass it down as `allowed`/`message` rather than duplicating the
 * disabled-plus-tooltip rendering themselves.
 */
export function GatedControl({ allowed, message, children, fallback }: GatedControlProps) {
  if (allowed) {
    return <>{children}</>;
  }

  const disabledElement = fallback ?? (isValidElement(children) ? cloneElement(children, { disabled: true, onClick: undefined } as any) : null);

  if (!disabledElement) {
    return null;
  }

  return <UpgradeTooltip message={message}>{disabledElement}</UpgradeTooltip>;
}

export default GatedControl;
